<template>
  <section class="public-status-page">
    <div class="hero-section">
      <div class="container">
        <router-link :to="{ name: 'FloodReliefMission' }" class="back-link">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          กลับหน้าหลักภารกิจ
        </router-link>
        <h1 class="hero-title">ตรวจสอบสถานะคำขอเบิกค่าเชื้อเพลิง</h1>
        <p class="hero-subtitle">
          ค้นหาคำขอด้วยเบอร์โทรผู้ประสานงาน
        </p>
      </div>
    </div>

    <div class="main-content">
      <!-- Search Form -->
      <div class="search-card">
        <form @submit.prevent="handleSearch" class="search-form">
          <div class="search-input-wrapper">
            <svg class="search-icon" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              v-model="searchQuery"
              type="text"
              placeholder="กรอกเบอร์โทรศัพท์ผู้ประสานงาน"
              class="search-input"
              :disabled="loading"
            />
            <button 
              v-if="searchQuery.length > 0" 
              type="button" 
              @click="clearSearch"
              class="clear-button"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <button type="submit" class="search-button" :disabled="loading || searchQuery.length < 3">
            <svg v-if="loading" class="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span v-else>ค้นหา</span>
          </button>
        </form>
        <p v-if="searchQuery.length > 0 && searchQuery.length < 3" class="search-hint">
          กรุณากรอกอย่างน้อย 3 ตัวอักษร
        </p>
      </div>

      <!-- Error Message -->
      <div v-if="error" class="error-box">
        <svg class="error-icon" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span>{{ error }}</span>
        <button type="button" @click="error = null" class="error-close">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Initial State -->
      <div v-if="!hasSearched && !loading" class="empty-state">
        <svg class="empty-icon" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
        </svg>
        <h3 class="empty-title">ค้นหาคำขอของคุณ</h3>
        <p class="empty-subtitle">เบอร์โทรผู้ประสานงานเพื่อตรวจสอบสถานะคำขอ</p>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <div class="loading-spinner"></div>
        <p class="loading-text">กำลังค้นหา...</p>
      </div>

      <!-- No Results -->
      <div v-if="hasSearched && !loading && requests.length === 0" class="empty-state">
        <svg class="empty-icon" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <h3 class="empty-title">ไม่พบคำขอ</h3>
        <p class="empty-subtitle">ไม่พบคำขอที่ตรงกับ "{{ lastSearchQuery }}"<br/>กรุณาตรวจสอบข้อมูลและลองใหม่อีกครั้ง</p>
      </div>

      <!-- Results -->
      <div v-if="!loading && requests.length > 0" class="results-section">
        <div class="results-header">
          <h2 class="results-title">พบ {{ pagination.total }} คำขอ</h2>
          <p class="results-subtitle">แสดงผลลัพธ์สำหรับ "{{ lastSearchQuery }}"</p>
        </div>

        <div class="results-list">
          <div v-for="request in requests" :key="request.id" class="request-card">
            <div class="card-header">
              <div>
                <span class="request-code">{{ request.request_code }}</span>
                <span :class="['status-badge', statusClass(request.status_label)]">
                  {{ statusLabel(request.status_label) }}
                </span>
              </div>
              <span class="request-date">{{ formatDate(request.created_at) }}</span>
            </div>
            
            <div class="card-body">
              <div class="info-grid">
                <div class="info-item">
                  <span class="info-label">องค์กร/กลุ่ม</span>
                  <span class="info-value">{{ request.organization_name }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">จังหวัดองค์กร</span>
                  <span class="info-value">{{ request.organization_province }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">ผู้ประสานงาน</span>
                  <span class="info-value">{{ request.coordinator_name }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">เบอร์โทร</span>
                  <span class="info-value">{{ request.coordinator_phone }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">วันที่ลงพื้นที่</span>
                  <span class="info-value">{{ formatDate(request.field_date) }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">ยอดคำขอ</span>
                  <span class="info-value amount">฿{{ formatCurrency(request.total_amount) }}</span>
                </div>
                <div v-if="request.approved_amount > 0" class="info-item">
                  <span class="info-label">ยอดอนุมัติ</span>
                  <span class="info-value amount approved">฿{{ formatCurrency(request.approved_amount) }}</span>
                </div>
              </div>

              <!-- Admin Notes -->
              <div v-if="request.admin_notes" class="admin-notes">
                <span class="notes-label">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                  </svg>
                  หมายเหตุจากเจ้าหน้าที่
                </span>
                <p class="notes-text">{{ request.admin_notes }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div v-if="pagination.last_page > 1" class="pagination">
          <button 
            type="button" 
            @click="goToPage(pagination.current_page - 1)"
            :disabled="pagination.current_page <= 1"
            class="page-button"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <span class="page-info">หน้า {{ pagination.current_page }} จาก {{ pagination.last_page }}</span>
          <button 
            type="button" 
            @click="goToPage(pagination.current_page + 1)"
            :disabled="pagination.current_page >= pagination.last_page"
            class="page-button"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Privacy Notice -->
      <div class="privacy-notice">
        <svg class="notice-icon" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
        <div>
          <p class="notice-title">ข้อมูลถูกปกป้อง</p>
          <p class="notice-text">เบอร์โทรศัพท์จะแสดงเฉพาะบางส่วนเพื่อความเป็นส่วนตัว ข้อมูลบัญชีธนาคารจะไม่แสดงในหน้านี้</p>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import FloodReliefMissionService from '@/services/FloodReliefMissionService'

const currencyFormatter = new Intl.NumberFormat('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const STATUS_LABELS = {
  pending: 'รอตรวจสอบ',
  approved: 'อนุมัติแล้ว',
  rejected: 'ไม่อนุมัติ',
  paid: 'โอนเงินแล้ว',
  evidence_needed: 'ขอหลักฐานเพิ่ม',
  unknown: 'ไม่ทราบสถานะ'
}

export default {
  name: 'FloodReliefMissionPublicStatus',
  data() {
    return {
      searchQuery: '',
      lastSearchQuery: '',
      loading: false,
      error: null,
      hasSearched: false,
      requests: [],
      pagination: {
        current_page: 1,
        last_page: 1,
        total: 0
      }
    }
  },
  mounted() {
    window.scrollTo(0, 0)
    // Check if there's a search query in URL
    if (this.$route.query.search) {
      this.searchQuery = this.$route.query.search
      this.handleSearch()
    }
  },
  methods: {
    async handleSearch(page = 1) {
      if (this.searchQuery.length < 3) {
        return
      }

      this.loading = true
      this.error = null
      this.lastSearchQuery = this.searchQuery

      try {
        const response = await FloodReliefMissionService.publicSearch(this.searchQuery, page)
        const data = response && response.data && response.data.data ? response.data.data : {}
        
        this.requests = data.data || []
        this.pagination = {
          current_page: data.current_page || 1,
          last_page: data.last_page || 1,
          total: data.total || 0
        }
        this.hasSearched = true

        // Update URL
        this.$router.replace({
          query: { search: this.searchQuery }
        }).catch(() => {})
      } catch (err) {
        if (err && err.response && err.response.status === 429) {
          this.error = 'คุณค้นหาบ่อยเกินไป กรุณารอสักครู่แล้วลองใหม่'
        } else if (err && err.response && err.response.data && err.response.data.message) {
          this.error = err.response.data.message
        } else {
          this.error = 'เกิดข้อผิดพลาดในการค้นหา กรุณาลองใหม่อีกครั้ง'
        }
      } finally {
        this.loading = false
      }
    },
    clearSearch() {
      this.searchQuery = ''
      this.requests = []
      this.hasSearched = false
      this.error = null
      this.$router.replace({ query: {} }).catch(() => {})
    },
    goToPage(page) {
      if (page < 1 || page > this.pagination.last_page) {
        return
      }
      this.handleSearch(page)
    },
    formatCurrency(value) {
      return currencyFormatter.format(value || 0)
    },
    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleDateString('th-TH', { year: 'numeric', month: 'short', day: 'numeric' })
    },
    statusLabel(statusKey) {
      return STATUS_LABELS[statusKey] || STATUS_LABELS.unknown
    },
    statusClass(statusKey) {
      const classes = {
        pending: 'status-pending',
        approved: 'status-approved',
        rejected: 'status-rejected',
        paid: 'status-paid',
        evidence_needed: 'status-evidence'
      }
      return classes[statusKey] || 'status-unknown'
    }
  }
}
</script>

<style scoped>
.public-status-page {
  background-color: #f9fafb;
  min-height: 100vh;
  padding-top: 5rem;
}

.hero-section {
  background: linear-gradient(to right, #6366f1, #8b5cf6);
  color: white;
  padding: 3rem 1rem;
}

.container {
  max-width: 1024px;
  margin: 0 auto;
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 1rem;
  transition: color 0.2s;
}

.back-link:hover {
  color: white;
}

.hero-title {
  font-size: 1.875rem;
  font-weight: 700;
}

@media (min-width: 768px) {
  .hero-title {
    font-size: 2.25rem;
  }
}

.hero-subtitle {
  margin-top: 0.5rem;
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.9);
}

.main-content {
  max-width: 1024px;
  margin: 0 auto;
  padding: 0 1rem 3rem;
  margin-top: -1.5rem;
}

.search-card {
  background: white;
  border-radius: 1rem;
  padding: 1.5rem;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
}

.search-form {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

@media (min-width: 640px) {
  .search-form {
    flex-wrap: nowrap;
  }
}

.search-input-wrapper {
  flex: 1;
  position: relative;
  min-width: 200px;
}

.search-icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  width: 1.25rem;
  height: 1.25rem;
  color: #9ca3af;
}

.search-input {
  width: 100%;
  padding: 0.875rem 2.5rem 0.875rem 3rem;
  border: 2px solid #e5e7eb;
  border-radius: 0.75rem;
  font-size: 1rem;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.search-input:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.search-input:disabled {
  background-color: #f9fafb;
}

.clear-button {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  padding: 0.25rem;
  color: #9ca3af;
  background: none;
  border: none;
  cursor: pointer;
  border-radius: 0.25rem;
}

.clear-button:hover {
  color: #6b7280;
}

.search-button {
  padding: 0.875rem 2rem;
  background: linear-gradient(to right, #6366f1, #8b5cf6);
  color: white;
  font-weight: 600;
  border: none;
  border-radius: 0.75rem;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 100px;
}

.search-button:hover:not(:disabled) {
  opacity: 0.9;
  transform: translateY(-1px);
}

.search-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.search-hint {
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: #f59e0b;
}

.error-box {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 1rem;
  padding: 1rem;
  background-color: #fef2f2;
  border: 1px solid #fee2e2;
  border-radius: 0.75rem;
  color: #dc2626;
  font-size: 0.875rem;
}

.error-icon {
  width: 1.25rem;
  height: 1.25rem;
  flex-shrink: 0;
}

.error-close {
  margin-left: auto;
  padding: 0.25rem;
  background: none;
  border: none;
  cursor: pointer;
  color: #dc2626;
}

.empty-state {
  text-align: center;
  padding: 4rem 1rem;
}

.empty-icon {
  width: 4rem;
  height: 4rem;
  margin: 0 auto;
  color: #d1d5db;
}

.empty-title {
  margin-top: 1rem;
  font-size: 1.25rem;
  font-weight: 600;
  color: #374151;
}

.empty-subtitle {
  margin-top: 0.5rem;
  color: #6b7280;
  font-size: 0.875rem;
}

.loading-state {
  text-align: center;
  padding: 4rem 1rem;
}

.loading-spinner {
  width: 3rem;
  height: 3rem;
  margin: 0 auto;
  border: 3px solid #e5e7eb;
  border-top-color: #6366f1;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-text {
  margin-top: 1rem;
  color: #6b7280;
}

.results-section {
  margin-top: 2rem;
}

.results-header {
  margin-bottom: 1rem;
}

.results-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
}

.results-subtitle {
  font-size: 0.875rem;
  color: #6b7280;
}

.results-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.request-card {
  background: white;
  border-radius: 1rem;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  border: 1px solid #f3f4f6;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 1rem 1.25rem;
  background: linear-gradient(to right, #f9fafb, #f3f4f6);
  border-bottom: 1px solid #e5e7eb;
}

.request-code {
  font-weight: 700;
  color: #111827;
  margin-right: 0.75rem;
}

.status-badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 9999px;
}

.status-pending {
  background-color: #fef3c7;
  color: #b45309;
}

.status-approved {
  background-color: #d1fae5;
  color: #047857;
}

.status-rejected {
  background-color: #fee2e2;
  color: #b91c1c;
}

.status-paid {
  background-color: #dbeafe;
  color: #1d4ed8;
}

.status-evidence {
  background-color: #fce7f3;
  color: #be185d;
}

.status-unknown {
  background-color: #e5e7eb;
  color: #6b7280;
}

.request-date {
  font-size: 0.875rem;
  color: #6b7280;
}

.card-body {
  padding: 1.25rem;
}

.info-grid {
  display: grid;
  gap: 0.75rem;
  grid-template-columns: repeat(2, 1fr);
}

@media (min-width: 640px) {
  .info-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.info-label {
  font-size: 0.75rem;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

.info-value {
  font-size: 0.875rem;
  color: #111827;
  font-weight: 500;
}

.info-value.amount {
  font-weight: 700;
  color: #0d9488;
}

.info-value.approved {
  color: #047857;
}

.admin-notes {
  margin-top: 1rem;
  padding: 1rem;
  background-color: #eff6ff;
  border-radius: 0.75rem;
  border-left: 4px solid #3b82f6;
}

.notes-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #1d4ed8;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

.notes-text {
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: #1e40af;
  white-space: pre-wrap;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 1.5rem;
}

.page-button {
  padding: 0.5rem;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;
}

.page-button:hover:not(:disabled) {
  background-color: #f9fafb;
  border-color: #d1d5db;
}

.page-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  font-size: 0.875rem;
  color: #6b7280;
}

.privacy-notice {
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  padding: 1rem;
  background-color: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 0.75rem;
}

.notice-icon {
  width: 1.5rem;
  height: 1.5rem;
  color: #16a34a;
  flex-shrink: 0;
}

.notice-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #166534;
}

.notice-text {
  font-size: 0.75rem;
  color: #15803d;
  margin-top: 0.25rem;
}

.animate-spin {
  animation: spin 1s linear infinite;
}
</style>
